import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import { Accordion, Badge, Radio, Text } from '@mantine/core';
import { IconArrowsSplit, IconBolt, IconSettings } from '@tabler/icons-react';
import { useMemo } from 'react';
import type { SuiteForm } from './form';
import type { WorkerMode } from './SuiteSettings';
import { WorkerCountSlider } from './WorkerCountSlider';
import { api } from '~/trpc-extra/api';

const Label = ({ label }: { label: string }) => {
  return (
    <Text
      size="sm"
      className="leading-[1.3] font-[var(--mantine-font-family-monospace)]"
    >
      {label}
    </Text>
  );
};

export function ParallelismSection({ form }: { form: SuiteForm }) {
  // Casting since we know this type is valid (and forms are loosely typed the way we're using them)
  const workerMode = form.values.workerMode as WorkerMode;
  const { data: entitlements, isLoading: isLoadingEntitlements } = api.team.getEntitlements.useQuery();

  const workerModeOptions: Record<
    WorkerMode,
    { label: React.ReactNode; description: string; icon: React.ReactNode; disabled: boolean }
  > = useMemo(
    () => ({
      auto: {
        label: (
          <div className="flex flex-row items-center gap-x-2">
            <Label label="Auto" />
            <Badge
              size="xs"
              variant="filled"
              color="blue"
            >
              Recommended
            </Badge>
          </div>
        ),
        disabled: isLoadingEntitlements,
        description: 'Enables high parallelism with smart worker count detection',
        icon: (
          <IconBolt
            size={20}
            className="text-blue-500"
          />
        ),
      },
      manual: {
        label: <Label label="Manual" />,
        description: 'Choose specific number of workers',
        disabled: isLoadingEntitlements,
        icon: (
          <IconSettings
            size={20}
            className="text-gray-500"
          />
        ),
      },
    }),
    [isLoadingEntitlements],
  );

  const [animationParent] = useAutoAnimate();
  return (
    <Accordion.Item value="parallelism">
      <Accordion.Control icon={<IconArrowsSplit size={14} />}>Parallelism Settings</Accordion.Control>
      <Accordion.Panel>
        <div
          className="flex flex-1 flex-col gap-4 px-1"
          ref={animationParent}
        >
          <div>
            <Text size="sm">Number of Workers</Text>

            <Text
              size="xs"
              c="dimmed"
            >
              Choose how many workers to allocate for your task. Auto mode will detect the optimal number based on your
              workload.
            </Text>
          </div>

          <Radio.Group
            value={workerMode}
            onChange={(value) => form.setFieldValue('workerMode', value as WorkerMode)}
            className="space-y-3"
          >
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {Object.entries(workerModeOptions).map(([key, opt]) => {
                return (
                  <Radio.Card
                    radius="md"
                    value={key}
                    key={key}
                  >
                    <div className="flex flex-row items-center gap-x-2">
                      <Radio.Indicator />
                      {opt.icon}
                      <div>
                        {opt.label}
                        <Text
                          size="xs"
                          c="dimmed"
                          className="mt-1 text-[var(--mantine-color-dimmed)]"
                        >
                          {opt.description}
                        </Text>
                      </div>
                    </div>
                  </Radio.Card>
                );
              })}
            </div>
          </Radio.Group>

          {workerMode === 'manual' && (
            <div className="mt-4">
              <Text
                size="sm"
                mb="xs"
              >
                Worker Count
              </Text>
              <WorkerCountSlider
                {...form.getInputProps('numberOfWorkers')}
                max={entitlements?.maxConcurrency}
              />
            </div>
          )}
        </div>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
